import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { BsEnvelopeSlash, BsEnvelopeX } from 'react-icons/bs';
import { Save, X } from 'lucide-react';
import Tooltip from '@/components/Tooltip';
import { GgcButton } from '@/components/ui/ggc/button';
import { GgcInput } from '@/components/ui/ggc/input';
import { RowActionButton } from '../RowActionButton';
import { PopoverShell } from '@/components/PopoverShell';
import ChannelTypeIcon from '@/components/icons/ChannelTypeIcon';
import { ChannelThumbnail } from '@/components/ChannelThumbnail';
import { useI18nText } from '@/lib/utils/i18nText';
import { loadPanelUser, savePanelUser } from '@/lib/api/panel.api';
import { getInfluencerEmails, isIncorrectEmail, NO_CONTACT_EMAIL } from '../../utils';
import type { Influencer } from '../../types';

/** Which of the two editable fields the "No Contact" mini-confirm popover targets. */
type NoContactField = 'email' | 'contactEmail';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Full user record returned by panel.loadUser — only the fields the panel.saveUser
 * full payload needs. The dashboard row (`Influencer`) does NOT carry contactEmail/
 * paypalEmail/skype/memo/blacklistedReason, so we must load the full record before
 * saving; otherwise the full-overwrite save would wipe those fields.
 */
interface FullPanelUser {
  _id?: string;
  email?: string;
  contactEmail?: string;
  selfProfile?: { email?: string };
  skype?: string;
  paypalEmail?: string;
  country?: string;
  lng?: string;
  socials?: Record<string, string | undefined>;
  blacklistedReason?: string;
  memo?: string;
}

export function EmailCell({ inf, selectedCampaignId, emptyDash }: {
  inf: Influencer;
  selectedCampaignId?: string;
  emptyDash: ReactNode;
}) {
  const { t } = useI18nText();
  const [open, setOpen] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [contactInput, setContactInput] = useState('');
  const [selfProfileInput, setSelfProfileInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* Which field the current error belongs to, so only that field's input gets the red border. */
  const [errorField, setErrorField] = useState<'email' | 'contactEmail' | 'selfProfileEmail' | null>(null);
  /* Which field's "No Contact" mini-confirm popover is currently open, if any. */
  const [noContactPopover, setNoContactPopover] = useState<NoContactField | null>(null);
  /* Optimistic override of the displayed email list after a successful save. */
  const [localEmails, setLocalEmails] = useState<string[] | null>(null);
  const fullUserRef = useRef<FullPanelUser | null>(null);
  const ch = inf.channels?.[0];
  const displayName = inf.profile?.fullname || inf.profile?.name || ch?.title || t('Unknown', '알 수 없음');

  // Displayed emails, or the optimistic override after a save.
  const emails = useMemo(
    () => localEmails ?? getInfluencerEmails(inf),
    [inf, localEmails],
  );

  const correctEmails = emails.filter((e) => !isIncorrectEmail(e));
  const hasOnlyIncorrect = correctEmails.length === 0 && emails.length > 0;

  // Campaign email status (sent date, subject, opened) for the selected campaign.
  const uc = inf.campaigns?.find((c) => c.campaignId === selectedCampaignId);
  const hasOpened = uc?.hasOpenedMessage;
  const lastMsg = uc?.lastMessage;
  const sentDate = lastMsg?.sentAt?.slice(0, 10);
  const showStatus = uc != null && hasOpened != null && (hasOpened === true || correctEmails.length > 0);

  // Loads the full user record (for the contact/self-reported email fields the
  // dashboard row doesn't carry) whenever the popover opens — PopoverShell/Radix
  // owns the open/close transition itself, this just reacts to it.
  useEffect(() => {
    setNoContactPopover(null);
    if (!open) return;
    setError(null);
    setErrorField(null);
    const cached = fullUserRef.current;
    if (cached) {
      setEmailInput(cached.email ?? '');
      setContactInput(cached.contactEmail ?? '');
      setSelfProfileInput(cached.selfProfile?.email ?? '');
      return;
    }
    // Prefill the registration email immediately from the row; the contact email
    // is only known once the full record loads.
    setEmailInput(inf.email ?? '');
    setContactInput('');
    setSelfProfileInput('');
    setLoading(true);
    (async () => {
      try {
        const res = await loadPanelUser<{ user: FullPanelUser }>(inf._id);
        const u = res?.user ?? null;
        fullUserRef.current = u;
        setEmailInput(u?.email ?? inf.email ?? '');
        setContactInput(u?.contactEmail ?? '');
        setSelfProfileInput(u?.selfProfile?.email ?? '');
      } catch {
        setError(t('Failed to load user', '사용자 정보를 불러오지 못했습니다'));
      } finally {
        setLoading(false);
      }
    })();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    const email = emailInput.trim().toLowerCase();
    const contactEmail = contactInput.trim().toLowerCase();
    const selfProfileEmail = selfProfileInput.trim().toLowerCase();
    // Registration email is the only one of the three that's required — confirmed by BE
    // rejecting an empty `email` with a 400 (admin/panel/save-user validates it as a
    // required email string). Contact/self-reported stay optional (empty is fine).
    if (!email) {
      setError(t("Registration email can't be empty — enter the corrected email instead of deleting it", '가입 이메일은 비워둘 수 없습니다 — 삭제 대신 올바른 이메일로 수정해 주세요'));
      setErrorField('email');
      return;
    }
    if (!EMAIL_RE.test(email)) {
      setError(t('Enter a valid registration email', '올바른 가입 이메일을 입력하세요'));
      setErrorField('email');
      return;
    }
    if (contactEmail && !EMAIL_RE.test(contactEmail)) {
      setError(t('Enter a valid contact email', '올바른 연락처 이메일을 입력하세요'));
      setErrorField('contactEmail');
      return;
    }
    if (selfProfileEmail && !EMAIL_RE.test(selfProfileEmail)) {
      setError(t('Enter a valid self-reported email', '올바른 본인 입력 이메일을 입력하세요'));
      setErrorField('selfProfileEmail');
      return;
    }
    const u = fullUserRef.current;
    if (!u) {
      setError(t('Failed to load user', '사용자 정보를 불러오지 못했습니다'));
      return;
    }
    const prevEmail = u.email;
    const prevContact = u.contactEmail;
    const prevSelfProfileEmail = u.selfProfile?.email;
    setSaving(true);
    setError(null);
    setErrorField(null);
    try {
      // Full payload — panel.saveUser overwrites the whole record, so every field
      // must be resent. Field mapping mirrors EditChannelDialog (keep them in sync).
      await savePanelUser({
        userId: inf._id,
        email,
        contactEmail,
        selfProfileEmail,
        skype: u.skype ?? '',
        paypalEmail: u.paypalEmail ?? '',
        country: u.country ?? '',
        lng: u.lng ?? '',
        blacklistedReason: u.blacklistedReason ?? '',
        memo: u.memo ?? '',
        youtube: u.socials?.youtube_com ?? '',
        twitch: u.socials?.twitch_tv ?? '',
        twitter: u.socials?.twitter_com ?? '',
        facebook: u.socials?.facebook_com ?? '',
        instagram: u.socials?.instagram_com ?? u.socials?.www_instagram_com ?? '',
        tiktok: u.socials?.tiktok_com ?? '',
        douyin: u.socials?.douyin_com ?? '',
        discord: u.socials?.discord_gg ?? '',
        telegram: u.socials?.t_me ?? '',
        bilibili: u.socials?.space_bilibili_com ?? u.socials?.bilibili_com ?? '',
        chzzk: u.socials?.chzzk_naver_com ?? '',
        afreeca: u.socials?.afreecatv_com ?? '',
      });
      fullUserRef.current = { ...u, email, contactEmail, selfProfile: { ...u.selfProfile, email: selfProfileEmail } };
      // Optimistically reflect all edited emails in the displayed list, swapping
      // the previous registration/contact/self-profile values for the new ones.
      const base = emails.slice();
      const next = base.map((e) => {
        if (prevEmail && e === prevEmail) return email;
        if (prevContact && e === prevContact) return contactEmail;
        if (prevSelfProfileEmail && e === prevSelfProfileEmail) return selfProfileEmail;
        return e;
      });
      if (email && !next.includes(email)) next.unshift(email);
      if (contactEmail && !next.includes(contactEmail)) next.push(contactEmail);
      if (selfProfileEmail && !next.includes(selfProfileEmail)) next.push(selfProfileEmail);
      setLocalEmails(Array.from(new Set(next.filter(Boolean))));
      setOpen(false);
    } catch {
      setError(t('Failed to save', '저장에 실패했습니다'));
    } finally {
      setSaving(false);
    }
  };

  // Which of the 3 source fields this chip's value came from — a value can match more than
  // one (e.g. registration and contact email are often the same address), which is also why
  // the chip list can show fewer entries than there are source fields (exact duplicates dedupe).
  const emailSourceLabel = (email: string): string => {
    const labels: string[] = [];
    if (inf.email && inf.email === email) labels.push(t('Registration email', '가입 이메일'));
    if (inf.contactEmail && inf.contactEmail === email) labels.push(t('Contact email', '연락처 이메일'));
    if (inf.selfProfile?.email && inf.selfProfile.email === email) labels.push(t('Self-reported email', '본인 입력 이메일'));
    return labels.length > 0 ? labels.join(' + ') : email;
  };

  // "No Contact" shortcut for the registration/contact email fields — mirrors
  // EditChannelDialog's fieldRow(opts.noContact) button + inline confirm popover
  // (keep the two in sync).
  const noContactButton = (field: NoContactField) => (
    <div className="relative">
      <Tooltip
        side="bottom"
        content={t(
          `Fills this field with a no-contact placeholder (${NO_CONTACT_EMAIL}) to mark that this influencer has no reachable email.`,
          `이 필드를 연락 불가 표시용 주소(${NO_CONTACT_EMAIL})로 채워, 연락 가능한 이메일이 없음을 표시합니다.`,
        )}
      >
        <button
          type="button"
          className="up-dialog-nocontact-btn"
          onClick={() => setNoContactPopover(noContactPopover === field ? null : field)}
        >
          <BsEnvelopeSlash className="mr-1 text-[12px]" />
          {t('No Contact', '연락 불가')}
        </button>
      </Tooltip>
      {noContactPopover === field && (
        <div className="absolute right-0 top-full z-10 mt-1 w-64 rounded-ggc-sm border border-ggc-border-gray bg-white p-3 shadow-ggc-md">
          <p className="mb-2 ggc-text-caption-m text-ggc-text-primary">
            {t(`Fill with ${NO_CONTACT_EMAIL}?`, `${NO_CONTACT_EMAIL} 으로 채울까요?`)}
          </p>
          <div className="flex justify-end gap-1.5">
            <button
              type="button"
              className="up-dialog-btn up-dialog-btn--sm"
              onClick={() => setNoContactPopover(null)}
            >
              {t('Cancel', '취소')}
            </button>
            <button
              type="button"
              className="up-dialog-btn up-dialog-btn--sm up-dialog-btn--primary"
              onClick={() => {
                if (field === 'email') setEmailInput(NO_CONTACT_EMAIL);
                else setContactInput(NO_CONTACT_EMAIL);
                if (error) { setError(null); setErrorField(null); }
                setNoContactPopover(null);
              }}
            >
              {t('Confirm', '확인')}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="email-cell">
      {hasOnlyIncorrect ? (
        <Tooltip content={`Incorrect emails: ${emails.join(', ')}`}>
          <span className="email-bad-icon">
            <BsEnvelopeX className="w-4 h-4" />
          </span>
        </Tooltip>
      ) : emails.length > 0 ? (
        <span className="email-chips">
          {emails.map((e) => (
            <Tooltip key={e} content={emailSourceLabel(e)}>
              <span className={`email-chip${isIncorrectEmail(e) ? ' email-chip--bad' : ''}`}>
                {e}
              </span>
            </Tooltip>
          ))}
        </span>
      ) : (
        <span className="email-address">{emptyDash}</span>
      )}

      {showStatus && (
        <div className="email-message-card">
          {sentDate && (
            <div className="email-message-row">
              <i className="fa fa-paper-plane email-message-icon" aria-hidden />
              <Tooltip content={t('Email sent at :', '이메일 발송 시각: ') + sentDate}>
                <span className="email-message-text">{sentDate}</span>
              </Tooltip>
            </div>
          )}
          {lastMsg?.subject && (
            <div className="email-message-row">
              <i className="fa fa-tag email-message-icon" aria-hidden />
              <Tooltip content={t('Email subject:', '이메일 제목: ') + lastMsg.subject}>
                <span className="email-message-text email-message-text--subject">
                  {lastMsg.subject}
                </span>
              </Tooltip>
            </div>
          )}
          <div className="email-message-row">
            {hasOpened === true ? (
              <>
                <i className="fa fa-envelope-open email-message-icon" aria-hidden style={{ color: 'var(--ggc-green)' }} />
                <Tooltip content={
                  lastMsg?.sentBy === 'influencer'
                    ? t('Influencer replied', `인플루언서 답장 확인 (${lastMsg.sentAt?.slice(0, 10) ?? ''})`)
                    : uc?.firstOpenedAt
                      ? t('Email opened by influencer (Sendgrid tracking)', `이메일 오픈됨 — Sendgrid 오픈 이벤트 확인 (${uc.firstOpenedAt.slice(0, 10)})`)
                      : t('Email opened by influencer (Sendgrid tracking)', '이메일 오픈됨 — Sendgrid 오픈 이벤트 확인')
                }>
                  <span className="email-message-text email-message-text--opened">{t('Opened', '오픈됨')}</span>
                </Tooltip>
              </>
            ) : (
              <>
                <i className="fa fa-envelope email-message-icon" aria-hidden style={{ color: 'var(--ggc-orange)' }} />
                <span className="email-message-text email-message-text--not-opened">{t('Not opened', '미오픈')}</span>
              </>
            )}
          </div>
        </div>
      )}

      <PopoverShell
        open={open}
        onOpenChange={setOpen}
        trigger={<RowActionButton>{t('Edit', '편집')}</RowActionButton>}
        side="bottom"
        align="start"
        width={280}
        maxWidth="min(300px, calc(100vw - 24px))"
        maxHeight="var(--radix-popper-available-height, 400px)"
      >
        <div className="flex flex-col" style={{ maxHeight: 'inherit' }}>
          {/* Identity header — same thumbnail + platform icon + name + close (✕)
              treatment as every other channel-row popover (EstPriceCell's
              Channel Rates, CampaignHistoryPopover, TagEditPopover). */}
          <div className="est-price-popover-header">
            <ChannelThumbnail src={ch?.thumbnail} />
            <ChannelTypeIcon type={ch?.type ?? 'youtube'} size={14} asBadge />
            <span className="est-price-popover-name">{displayName}</span>
            <button
              type="button"
              className="est-price-popover-close"
              onClick={() => setOpen(false)}
              aria-label={t('Close', '닫기')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="email-edit-popover-body overflow-y-auto">
            {loading ? (
              <p className="email-edit-loading">{t('Loading...', '불러오는 중...')}</p>
            ) : (
              <>
                <div className="email-edit-label-row">
                  <label className="email-edit-label">
                    {t('Registration email', '가입 이메일')}
                    <Tooltip content={t(
                      'The email used to sign up / log into this account.',
                      '이 계정을 가입·로그인할 때 사용하는 이메일입니다.',
                    )}>
                      <i className="fa fa-info-circle email-edit-label-tooltip-icon" aria-hidden />
                    </Tooltip>
                  </label>
                  {noContactButton('email')}
                </div>
                <GgcInput
                  type="email"
                  value={emailInput}
                  error={errorField === 'email'}
                  placeholder="name@example.com"
                  onChange={(e) => { setEmailInput(e.target.value); if (error) { setError(null); setErrorField(null); } }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
                  autoFocus
                />
                <div className="email-edit-label-row email-edit-label-row--spaced">
                  <label className="email-edit-label">
                    {t('Contact email', '연락처 이메일')}
                    <Tooltip content={t(
                      'A separate address for GGC to reach out to this influencer, if different from their registration email.',
                      '가입 이메일과 다를 경우 GGC가 이 인플루언서에게 연락할 때 사용하는 별도 이메일입니다.',
                    )}>
                      <i className="fa fa-info-circle email-edit-label-tooltip-icon" aria-hidden />
                    </Tooltip>
                  </label>
                  {noContactButton('contactEmail')}
                </div>
                <GgcInput
                  type="email"
                  value={contactInput}
                  error={errorField === 'contactEmail'}
                  placeholder="name@example.com"
                  onChange={(e) => { setContactInput(e.target.value); if (error) { setError(null); setErrorField(null); } }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
                />
                <label className="email-edit-label email-edit-label--spaced">
                  {t('Self-reported email', '본인 입력 이메일')}
                  <Tooltip content={t(
                    'Entered by the influencer themselves via their self-service profile — not verified by an admin.',
                    '인플루언서가 본인 프로필 폼에서 직접 입력한 이메일이며, 관리자가 검증한 값이 아닙니다.',
                  )}>
                    <i className="fa fa-info-circle email-edit-label-tooltip-icon" aria-hidden />
                  </Tooltip>
                </label>
                <GgcInput
                  type="email"
                  value={selfProfileInput}
                  error={errorField === 'selfProfileEmail'}
                  placeholder="name@example.com"
                  onChange={(e) => { setSelfProfileInput(e.target.value); if (error) { setError(null); setErrorField(null); } }}
                  onKeyDown={(e) => { if (e.key === 'Enter') void handleSave(); }}
                />
                {error && <p className="email-edit-error">{error}</p>}
                <div className="email-edit-actions">
                  <GgcButton variant="secondary" size="xs" onClick={() => setOpen(false)} disabled={saving}>
                    <X className="h-3 w-3" />{t('Cancel', '취소')}
                  </GgcButton>
                  <GgcButton variant="primary" size="xs" onClick={() => void handleSave()} disabled={saving || loading}>
                    <Save className="h-3 w-3" />{saving ? t('Saving...', '저장 중...') : t('Save', '저장')}
                  </GgcButton>
                </div>
              </>
            )}
          </div>
        </div>
      </PopoverShell>
    </div>
  );
}
